import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { ClientService, FacilityService } from '/src/api';
import {
  AddEquipmentModal,
  Box,
  BreadcrumbNav,
  EquipmentList,
  InfoBox,
  ListPage,
  PageHeader,
  Text,
} from '/src/components';
import { config } from '/src/config';
import { useGlobalStore, useUserStore } from '/src/context';
import { useProductTour } from '/src/hooks/useProductTour';
import { Facility } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { ReportingPeriodType } from '/src/lib/types';
import { getFacilityLabel, getPageTitle } from '/src/utils';
import { equipmentListTour } from '/src/utils/productTours';

export const ListEquipmentPage = observer(() => {
  /** Context **/
  const globalStore = useGlobalStore();
  const userStore = useUserStore();
  const params = useParams();

  /** State **/
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [clientState, setClientState] = useState<{ clientId: number; name: string; isDemo: boolean }>();
  const [facility, setFacility] = useState<Facility>();
  /** Computed **/
  const clientId = parseInt(params.client_id ?? '');
  const facilityId = parseInt(params.facility_id ?? '');
  const clientName = clientState?.clientId === clientId ? clientState.name : '';
  const isDemoClient = clientState?.clientId === clientId && clientState.isDemo;
  const isLoading = !(clientName && facility);
  const facilityName = facility ? getFacilityLabel(facility) : '';
  const isCanada = facility?.reporting_period_type_id === ReportingPeriodType.Yearly;

  const titleBar = useMemo(() => {
    const badges: ReactNode[] = [];

    if (isDemoClient) {
      badges.push(
        <InfoBox
          key="demo"
          pad={{ horizontal: '0.5rem' }}
          gap="xsmall"
          background={{ color: 'status-critical', opacity: 'medium' }}
          border={{ color: 'status-critical' }}
        >
          <Text size="0.95rem" weight={600} toUpperCase>
            Demo Client
          </Text>
        </InfoBox>
      );
    }

    return (
      <Box gap="small" direction="row">
        {badges}
      </Box>
    );
  }, [isDemoClient]);

  /** Effects **/
  useEffect(() => {
    if (!userStore.user) return;

    if (userStore.isExternalUser) {
      const client = userStore.getUserClient(clientId);
      setClientState({ clientId, name: client?.name ?? '', isDemo: client?.is_demo ?? false });
      return;
    }

    let isCurrent = true;
    ClientService.get({ id: clientId })
      .then((client) => {
        if (isCurrent) setClientState({ clientId, name: client.name, isDemo: client.is_demo ?? false });
      })
      .catch((err) => {
        if (isCurrent) globalStore.handleApiError(err, toastMessages.listFacilities.error);
      });

    return () => {
      isCurrent = false;
    };
  }, [clientId, userStore.user, userStore.isExternalUser, userStore.getUserClient, globalStore]);

  useEffect(() => {
    if (facilityName) {
      document.title = getPageTitle(config.client.basePageTitle, 'Equipment', facilityName);
    }
  }, [facilityName]);

  useEffect(() => {
    if (!facility) {
      FacilityService.get({ id: facilityId })
        .then((facility) => setFacility(facility))
        .catch((err) => globalStore.handleApiError(err, toastMessages.listFacilities.error));
    }
  }, [facility]);

  /* Product Tour */
  useProductTour({
    tourName: 'equipment-list-tour',
    steps: equipmentListTour,
    autoStart: true,
    showOnce: true,
  });

  /** Render **/
  return (
    <ListPage
      title={facilityName}
      pageHeader={
        <PageHeader
          title={facilityName}
          titleBar={titleBar}
          showAddButton={userStore.isAdminUser && facility?.is_active}
          addButtonLabel="ADD EQUIPMENT"
          onAddButton={() => setShowEquipmentModal(true)}
          dataTour="add-equipment-button"
        />
      }
      breadcrumbNav={
        <BreadcrumbNav
          previousPages={[
            { name: 'Clients', link: !userStore.isClientUser && !userStore.isFacilityUser ? '/clients' : undefined },
            { name: clientName, link: !userStore.isFacilityUser ? `/clients/${clientId}` : undefined },
            {
              name: 'Facilities',
              link: `/clients/${clientId}/facilities`,
            },
            {
              name: facilityName,
              link: `/clients/${clientId}/facilities/${facilityId}`,
            },
          ]}
          currentPageName="Equipment"
        />
      }
      isLoading={isLoading}
    >
      <Box flex="grow">
        <EquipmentList
          id="equipment_list"
          title="Asset List"
          clientId={clientId}
          facilityId={facilityId}
          isCanada={isCanada}
          firstReportingQuarter={facility?.first_active_reporting_quarter}
          lastReportingQuarter={facility?.last_active_reporting_quarter}
          showBulkUpdateButton
          showSearchInput
          standalone
        />
      </Box>
      {showEquipmentModal && !!clientId && !!facilityId && (
        <AddEquipmentModal clientId={clientId} facilityId={facilityId} setIsVisible={setShowEquipmentModal} />
      )}
    </ListPage>
  );
});
